import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Eye, Database, UserCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ThemeToggle from '../components/ThemeToggle';
import BrandLogo from '../components/BrandLogo';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-header" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="flex items-center gap-1 sm:gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/50 rounded-2xl mb-4">
              <Lock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-4">Privacy Policy</h1>
            <p className="text-slate-600 dark:text-slate-400">Last updated: January 2026</p>
          </div>

          {/* Content */}
          <Card className="mb-8 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6 sm:p-8 space-y-8">
              {/* Introduction */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Introduction</h2>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                  At KaiCheckIt, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, 
                  and safeguard your information when you use Kai, our AI-powered verification assistant. Please read this privacy 
                  policy carefully. If you do not agree with the terms of this privacy policy, please do not access the application.
                </p>
              </section>

              {/* Information We Collect */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <Database className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Information We Collect</h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-2">Personal Information</h3>
                    <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                      When you register for an account, we collect personal information including your name, email address, 
                      and authentication credentials. If you use social login (Google or Facebook), we receive basic profile 
                      information from those services.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-2">Usage Information</h3>
                    <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                      We collect information about how you use our service, including the claims you verify, search queries, 
                      images uploaded, and interaction patterns. This helps us improve our AI algorithms and service quality.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-2">Technical Information</h3>
                    <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                      We automatically collect technical information such as IP address, browser type, device information, 
                      operating system, and cookies to ensure proper functionality and security of our service.
                    </p>
                  </div>
                </div>
              </section>

              {/* How We Use Your Information */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <UserCheck className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">How We Use Your Information</h2>
                </div>
                <ul className="space-y-2 text-slate-600 dark:text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span>To provide, operate, and maintain our verification service</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span>To improve, personalize, and expand our AI algorithms and service features</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span>To process your transactions and manage your subscription</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span>To send you updates, security alerts, and administrative messages</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span>To detect, prevent, and address technical issues and security threats</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span>To comply with legal obligations and enforce our terms</span>
                  </li>
                </ul>
              </section>

              {/* Data Sharing */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <Eye className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Data Sharing and Disclosure</h2>
                </div>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                  We do not sell your personal information. We may share your information in the following circumstances:
                </p>
                <ul className="space-y-2 text-slate-600 dark:text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span><strong>Service Providers:</strong> With trusted third-party services that help us operate our platform (e.g., cloud hosting, payment processing)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</span>
                  </li>
                </ul>
              </section>

              {/* Your Rights (GDPR) */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Your Rights (GDPR Compliance)</h2>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                  If you are a resident of the European Economic Area (EEA), you have certain data protection rights:
                </p>
                <ul className="space-y-2 text-slate-600 dark:text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span><strong>Access:</strong> Request copies of your personal data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span><strong>Rectification:</strong> Request correction of inaccurate data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span><strong>Erasure:</strong> Request deletion of your data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span><strong>Restriction:</strong> Request restriction of processing your data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span><strong>Portability:</strong> Request transfer of your data to another service</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 mt-1">•</span>
                    <span><strong>Objection:</strong> Object to processing of your data</span>
                  </li>
                </ul>
              </section>

              {/* Data Security */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Data Security</h2>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                  We implement appropriate technical and organizational security measures to protect your personal information. 
                  This includes encryption, secure servers, regular security audits, and access controls. However, no method of 
                  transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                </p>
              </section>

              {/* GDPR Compliance */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">GDPR Compliance</h2>
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 dark:text-blue-200 font-medium">
                    KaiCheckIt is committed to GDPR compliance for all users.
                  </p>
                </div>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                  For users in the European Economic Area (EEA), United Kingdom, and Switzerland, we process personal data in accordance 
                  with the General Data Protection Regulation (GDPR). As part of our commitment to data protection:
                </p>
                <ul className="space-y-2 text-slate-600 dark:text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                    <span>We provide complete <strong>data export</strong> functionality (JSON format) for all account holders</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                    <span>You can <strong>delete your account</strong> and all associated data at any time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                    <span>We use <strong>explicit consent</strong> for cookies and data processing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                    <span>We minimize data collection to only what is necessary for service delivery</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                    <span>Pro users have access to enhanced data management features</span>
                  </li>
                </ul>
              </section>

              {/* Data Retention */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Data Retention</h2>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                  We retain your personal information only for as long as necessary to fulfill the purposes outlined in this 
                  privacy policy, unless a longer retention period is required by law. When you delete your account, we will 
                  delete or anonymize your data within 30 days.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Contact Us</h2>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                  If you have questions about this Privacy Policy or wish to exercise your data rights, please contact us at:
                  <br />
                  <a href="mailto:Admin@Kaiventive.com" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">Admin@Kaiventive.com</a>
                </p>
              </section>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md mt-16">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <BrandLogo variant="plain" data-testid="brand-logo-footer" />
              <span className="text-slate-600 dark:text-slate-400">© 2025 Kaiventive Holdings</span>
            </div>
            <div className="flex gap-6">
              <a href="/cookies" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Cookies</a>
              <a href="/terms" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
